/**
 * What the unused days are worth.
 *
 * ---------------------------------------------------------------------
 * THE MOVEMENT IS THE HEADLINE, NOT THE BALANCE
 *
 * The obvious thing to put in the largest type is what the club owes,
 * and it is the wrong thing. The balance is the same number it was
 * yesterday and will be tomorrow; it is a fact about the balance sheet
 * and it does not require anybody to do anything.
 *
 * What posts is the CHANGE, and the change is the thing that could go
 * wrong. A valuation that posted its balance rather than its movement
 * would put the whole liability on the P&L every time somebody pressed
 * the button, and the second press would double a five-figure debt in a
 * way nobody would notice until the audit. So the movement is shown
 * beside the total, with the date it moved from, and posting says which
 * of the two it is about to write.
 *
 * ---------------------------------------------------------------------
 * A SHORT FIGURE SAYS SO, LOUDLY
 *
 * 0147 deliberately values a club that has somebody with no rate on
 * file, because refusing would stop the club closing its books over one
 * unkeyed salary. That is the right call and it makes this screen
 * responsible for the consequence: a total that is quietly missing four
 * people is worse than no total at all.
 *
 * So `understated` is a banner rather than a footnote, it names how many
 * are missing, and it is above the number it applies to. The journal
 * entry's own memo carries the same sentence for whoever reads it in six
 * months without this screen in front of them.
 *
 * ---------------------------------------------------------------------
 * WHAT ANY ONE PERSON'S DAYS ARE WORTH IS BEHIND A SECOND CLICK
 *
 * Same rule as Labor Cost and the pay history on Personnel, for the same
 * reason: a per-person valuation divided by the banked hours is that
 * person's pay rate, and every one of those reads writes an audit row
 * naming the reader. A log full of reads nobody meant to make is a log
 * nobody can use.
 */

import { useState } from 'react';
import { api } from '../lib/api.ts';
import { useApi, useMutation } from '../lib/useApi.ts';
import { date as fmtDate, money, titleCase } from '../lib/format.ts';
import { useSession } from '../lib/session.tsx';
import { Card, Empty, ErrorBanner, Loading, Status, Tile } from '../components/ui.tsx';

interface Run {
  id: string;
  as_of: string;
  status: string;
  total_minor: number;
  movement_minor: number;
  staff_valued: number;
  staff_unpriced: number;
  understated: boolean;
  previous_as_of: string | null;
  journal_entry_id: string | null;
  entry_number: number | null;
  computed_at: string;
  posted_at: string | null;
  reversed_at: string | null;
  reversed_reason: string | null;
}

interface DeptMovement {
  department_id: string;
  department_code: string;
  department: string;
  now_minor: number;
  then_minor: number;
  movement_minor: number;
}

interface Blocker {
  reason: string;
  detail: string | null;
  affected: number;
}

interface Person {
  staff_member_id: string;
  display_name: string;
  job_title: string;
  department: string;
  leave_type: string;
  balance_minutes: number;
  basis: string;
  amount_minor: number;
}

const hours = (minutes: number) => `${(minutes / 60).toFixed(1)}h`;

/**
 * A blocker in the words of whoever has to act on it.
 *
 * The database returns a machine reason and a detail; the reason is what
 * turns a count into a sentence. Written here rather than in SQL for
 * `LaborCost.tsx`'s reason — it is a label, and labels follow the club's
 * vocabulary.
 */
function blockerText(b: Blocker): string {
  switch (b.reason) {
    case 'rollup_department':
      return `${b.affected} ${b.affected === 1 ? 'person has' : 'people have'} a balance and are filed under a department money cannot land on — ${b.detail}`;
    case 'later_valuation_exists':
      return `${b.detail}. Valuing an earlier date would measure the movement from a baseline in the future.`;
    default:
      return `${b.reason}${b.detail ? ` — ${b.detail}` : ''}`;
  }
}

/** Today, as the date input wants it. */
function today(): string {
  return new Date().toISOString().slice(0, 10);
}

export function LeaveLiabilityPage() {
  const { currency, atLeast } = useSession();
  const [asOf, setAsOf] = useState(today);
  const [openRun, setOpenRun] = useState<string | null>(null);
  const [refused, setRefused] = useState<{ date: string; list: Blocker[] } | null>(null);

  const mayValue = atLeast('controller');

  const runs = useApi<{ runs: Run[] }>('/v1/leave/liability/runs');
  const blockers = useApi<{ as_of: string; blockers: Blocker[] }>(
    `/v1/leave/liability/blockers?as_of=${asOf}`,
    [asOf],
  );

  const value = useMutation(async () => {
    const r = await api<{ valued: boolean; run?: Run; blockers?: Blocker[] }>(
      '/v1/leave/liability/runs',
      { method: 'POST', body: { as_of: asOf } },
    );
    if (r.valued && r.run) {
      setRefused(null);
      setOpenRun(r.run.id);
    } else {
      setRefused({ date: asOf, list: r.blockers ?? [] });
    }
    runs.reload();
    blockers.reload();
    return r;
  });

  if (runs.loading && !runs.data) return <Loading />;

  const all = runs.data?.runs ?? [];
  const live = all.filter((r) => r.status !== 'reversed');
  const latest = live.find((r) => r.status === 'posted') ?? null;
  const inTheWay = blockers.data?.blockers ?? [];

  return (
    <>
      <h1>Leave Liability</h1>
      <ErrorBanner error={runs.error ?? blockers.error ?? value.error} />

      <p className="muted small">
        Everybody&rsquo;s unused paid leave, priced against the rates on file. An hourly
        person&rsquo;s banked hour is worth their rate; a salaried one&rsquo;s is their salary over
        2,080 hours. The debt lands on Accrued Paid Time Off and the expense on Employee Benefits,
        never on wages — 6000 keeps meaning hours somebody actually worked.
      </p>

      <div className="tiles">
        <Tile
          label="Owed as of"
          value={latest ? money(latest.total_minor, currency) : '—'}
          note={latest ? fmtDate(latest.as_of) : 'nothing posted yet'}
        />
        <Tile
          label="Last movement"
          value={latest ? money(latest.movement_minor, currency) : '—'}
          note={latest?.previous_as_of ? `since ${fmtDate(latest.previous_as_of)}` : 'the first one'}
        />
        <Tile label="People valued" value={latest ? String(latest.staff_valued) : '—'} />
      </div>

      {/* ============================================================ */}
      <Card title="Value It">
        <div className="row" style={{ gap: 12, alignItems: 'flex-end' }}>
          <label className="field" style={{ marginBottom: 0 }}>
            <span>As of</span>
            <input
              type="date"
              value={asOf}
              onChange={(e) => setAsOf(e.target.value)}
              style={{ maxWidth: 190 }}
            />
          </label>
          {mayValue && (
            <button disabled={value.busy || inTheWay.length > 0} onClick={() => value.run()}>
              {value.busy ? 'Valuing…' : 'Value the balance'}
            </button>
          )}
        </div>
        <p className="muted small">
          The day the books are being closed. The balance is read as it stood on that date, so a
          holiday booked for August is not owed in January.
        </p>

        {inTheWay.length > 0 && (
          <div className="notice notice--warn">
            <strong>{fmtDate(asOf)} cannot be valued yet.</strong>
            <ul>
              {inTheWay.map((b) => (
                <li key={b.reason}>{blockerText(b)}</li>
              ))}
            </ul>
          </div>
        )}

        {/* The 409, spelled out. Kept separate from the list above
            because that one is what the club looks like now and this one
            is what happened when somebody pressed the button. */}
        {refused && refused.list.length > 0 && (
          <div className="notice notice--warn">
            <strong>{fmtDate(refused.date)} was not valued.</strong>
            <ul>
              {refused.list.map((b) => (
                <li key={b.reason}>{blockerText(b)}</li>
              ))}
            </ul>
          </div>
        )}

        {inTheWay.length === 0 && !refused && (
          <p className="muted small">
            Somebody with a balance and no pay rate does not stop this. They are counted, named on
            the valuation, and left out of the money — refusing would stop the club closing its
            books over one salary nobody has keyed in.
          </p>
        )}
      </Card>

      {/* ============================================================ */}
      <Card title="What Has Been Valued">
        {all.length === 0 ? (
          <Empty>The liability has never been valued. 2070 is empty.</Empty>
        ) : (
          <div className="table">
            <table className="data data--cards">
              <thead>
                <tr>
                  <th>As of</th>
                  <th className="num">Owed</th>
                  <th className="num">Movement</th>
                  <th className="num">People</th>
                  <th>State</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {all.map((r) => (
                  <tr key={r.id} className={r.status === 'reversed' ? 'muted' : undefined}>
                    <td data-label="As of" className="cell--title">
                      {fmtDate(r.as_of)}
                    </td>
                    <td data-label="Owed" className="num">
                      {money(r.total_minor, currency)}
                    </td>
                    <td data-label="Movement" className="num">
                      {money(r.movement_minor, currency)}
                    </td>
                    <td data-label="People" className="num">
                      {r.staff_valued}
                      {r.staff_unpriced > 0 && (
                        <span className="muted small"> · {r.staff_unpriced} unpriced</span>
                      )}
                    </td>
                    <td data-label="State">
                      <Status
                        tone={
                          r.status === 'posted'
                            ? 'good'
                            : r.status === 'reversed'
                              ? 'neutral'
                              : 'warning'
                        }
                      >
                        {titleCase(r.status)}
                      </Status>
                      {r.entry_number && (
                        <span className="muted small"> · entry {r.entry_number}</span>
                      )}
                    </td>
                    <td className="cell--action">
                      <button
                        className="linkish"
                        onClick={() => setOpenRun(openRun === r.id ? null : r.id)}
                      >
                        {openRun === r.id ? 'Close' : 'Open'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {openRun && (
        <RunDetail
          runId={openRun}
          mayValue={mayValue}
          onChanged={() => {
            runs.reload();
            blockers.reload();
          }}
        />
      )}
    </>
  );
}

// =====================================================================
// One valuation
// =====================================================================

function RunDetail({
  runId,
  mayValue,
  onChanged,
}: {
  runId: string;
  mayValue: boolean;
  onChanged: () => void;
}) {
  const { currency } = useSession();
  const [people, setPeople] = useState(false);
  const [reason, setReason] = useState('');
  const [reversing, setReversing] = useState(false);

  const { data, error, loading, reload } = useApi<{
    run: Run;
    departments: DeptMovement[];
    may_see_people: boolean;
  }>(`/v1/leave/liability/runs/${runId}`, [runId]);

  const post = useMutation(async () => {
    const r = await api(`/v1/leave/liability/runs/${runId}/post`, { method: 'POST' });
    reload();
    onChanged();
    return r;
  });

  const discard = useMutation(async () => {
    const r = await api(`/v1/leave/liability/runs/${runId}`, { method: 'DELETE' });
    onChanged();
    return r;
  });

  const reverse = useMutation(async () => {
    const r = await api(`/v1/leave/liability/runs/${runId}/reverse`, {
      method: 'POST',
      body: { reason },
    });
    setReversing(false);
    setReason('');
    reload();
    onChanged();
    return r;
  });

  if (loading && !data) return <Loading rows={2} />;
  if (!data) return null;

  const { run, departments } = data;

  return (
    <Card title={`As of ${fmtDate(run.as_of)}`}>
      <ErrorBanner error={error ?? post.error ?? discard.error ?? reverse.error} />

      {/* Above the number it applies to, not under it. */}
      {run.understated && (
        <div className="notice notice--warn">
          <strong>
            This figure is short. {run.staff_unpriced}{' '}
            {run.staff_unpriced === 1 ? 'person has' : 'people have'} a balance and no pay rate on
            file.
          </strong>
          <p className="muted small">
            They are counted here and left out of the money, because refusing to value would stop
            you closing the books over a salary nobody has keyed in. Put the rates on and value it
            again — the same sentence is written into the journal entry&rsquo;s memo, for whoever
            reads it without this screen in front of them.
          </p>
        </div>
      )}

      {run.status === 'draft' && (
        <p className="muted small">
          A draft. Nothing has reached the ledger — if the number is wrong the fix is a rate or a
          balance, not a correcting entry.
        </p>
      )}
      {run.status === 'reversed' && (
        <p className="muted small">
          Reversed {fmtDate(run.reversed_at)}: {run.reversed_reason}
        </p>
      )}

      <div className="tiles">
        <Tile label="Owed in total" value={money(run.total_minor, currency)} />
        <Tile
          label="Moved"
          value={money(run.movement_minor, currency)}
          note={
            run.previous_as_of
              ? `since ${fmtDate(run.previous_as_of)}`
              : 'the first valuation, so the whole balance'
          }
        />
        <Tile label="Valued" value={String(run.staff_valued)} />
        <Tile
          label="Unpriced"
          value={String(run.staff_unpriced)}
          note={run.staff_unpriced > 0 ? 'counted, not valued' : undefined}
        />
      </div>

      <p className="muted small">
        The movement is what posts, not the total. Posting the balance every time would put the
        whole debt on the books again at every valuation.
      </p>

      <div className="table">
        <table className="data data--cards">
          <thead>
            <tr>
              <th>Department</th>
              <th className="num">Was</th>
              <th className="num">Now</th>
              <th className="num">Movement</th>
            </tr>
          </thead>
          <tbody>
            {departments.map((d) => (
              <tr key={d.department_id}>
                <td data-label="Department" className="cell--title">
                  {d.department}
                </td>
                <td data-label="Was" className="num muted">
                  {money(d.then_minor, currency)}
                </td>
                <td data-label="Now" className="num">
                  {money(d.now_minor, currency)}
                </td>
                <td data-label="Movement" className="num">
                  {money(d.movement_minor, currency)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {data.may_see_people && (
        <div className="actions">
          <button className="secondary small" onClick={() => setPeople(!people)}>
            {people ? 'Hide' : 'Show what each person’s days are worth'}
          </button>
        </div>
      )}

      {people && <People runId={runId} />}

      {mayValue && (
        <div className="actions">
          {run.status === 'draft' && (
            <>
              <button disabled={post.busy} onClick={() => post.run()}>
                {post.busy
                  ? 'Posting…'
                  : `Post ${money(run.movement_minor, currency)} to the ledger`}
              </button>
              <button className="secondary" disabled={discard.busy} onClick={() => discard.run()}>
                Throw this draft away
              </button>
            </>
          )}

          {run.status === 'posted' &&
            (!reversing ? (
              <button className="secondary" onClick={() => setReversing(true)}>
                Reverse it
              </button>
            ) : (
              <>
                <label className="field">
                  <span>Why</span>
                  <input
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="Valued before the seasonal rates were keyed"
                  />
                  <div className="hint">
                    This is the only record of why the balance sheet moved, so it is required.
                    Reversing also frees the date to be valued again.
                  </div>
                </label>
                <button disabled={!reason.trim() || reverse.busy} onClick={() => reverse.run()}>
                  {reverse.busy ? 'Reversing…' : 'Reverse it'}
                </button>
                <button className="secondary" onClick={() => setReversing(false)}>
                  Cancel
                </button>
              </>
            ))}
        </div>
      )}
    </Card>
  );
}

// =====================================================================
// Per person
// =====================================================================

function People({ runId }: { runId: string }) {
  const { currency } = useSession();
  const { data, error, loading } = useApi<{ people: Person[] }>(
    `/v1/leave/liability/runs/${runId}/people`,
    [runId],
  );

  if (loading && !data) return <Loading rows={1} />;

  return (
    <div>
      <ErrorBanner error={error} />
      <p className="muted small">
        This read is recorded — who opened it, when, and for which valuation. The figures
        themselves are never written to the log.
      </p>
      <div className="table">
        <table className="data">
          <thead>
            <tr>
              <th>Who</th>
              <th>Department</th>
              <th>Kind</th>
              <th className="num">Banked</th>
              <th className="num">Worth</th>
            </tr>
          </thead>
          <tbody>
            {(data?.people ?? []).map((p) => (
              <tr key={`${p.staff_member_id}-${p.leave_type}`}>
                <td className="cell--title">
                  {p.display_name}
                  <span className="muted small"> · {p.job_title}</span>
                </td>
                <td className="muted">{p.department}</td>
                <td className="muted">{p.leave_type}</td>
                <td className="num">
                  {hours(Number(p.balance_minutes))}
                  {p.basis === 'salary' && <span className="muted small"> · salaried</span>}
                </td>
                <td className="num">{money(p.amount_minor, currency)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
